import React, { useEffect, useState, useRef } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import classNames from "classnames";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { Input } from "@design-system/Input/Input";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { isFreshbooksRegistrationVisitor } from "@helpers/freshbooks";
import styles from "./Register.module.scss";

interface Props {
    onSubmit(email: string): void | Promise<void>;
    initialEmail?: string;
}

interface FormSchema {
    email: string;
}

const registerWithEmailSchema = z.object({
    email: z
        .string()
        .min(1, "Email is required")
        .email("Provide a valid email"),
});

export const RegisterWithEmail: React.FC<Props> = ({
    onSubmit,
    initialEmail,
}) => {
    const [touched, setTouched] = useState(!!initialEmail);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const isFreshbooks = isFreshbooksRegistrationVisitor();

    const emailPlaceholder = isFreshbooks
        ? "Enter your FreshBooks email"
        : "Enter your email";

    const formik = useFormik<FormSchema>({
        initialValues: {
            email: initialEmail ?? "",
        },
        validateOnMount: true,
        validate: validateFormikWithZodSchema(registerWithEmailSchema),
        onSubmit: submitHelper({
            handler: async ({ email }) => {
                await onSubmit(email);
            },
        }),
    });
    useEffect(() => {
        if (initialEmail && emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, [initialEmail]);

    return (
        <FormikProvider value={formik}>
            <Form
                className={classNames(styles.form, {
                    [styles.touched]: touched,
                })}
            >
                <Input
                    {...formik.getFieldProps("email")}
                    ref={emailInputRef}
                    size="xl"
                    placeholder={emailPlaceholder}
                    aria-label="Email"
                    className={styles.emailInput}
                    onFocus={() => setTouched(true)}
                    autoComplete="email"
                />

                <ButtonWithLoader
                    loading={formik.isSubmitting}
                    type="submit"
                    variant="primary"
                    size="xl"
                    disabled={!formik.isValid}
                    className={styles.submitButton}
                    hotKey="enter"
                    hotKeyOptions={{ description: "Continue" }}
                    hotKeyDisplayPosition="inline"
                >
                    Continue
                </ButtonWithLoader>
            </Form>
        </FormikProvider>
    );
};
